import { useEffect, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Ruler, Home, Layers } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useParams } from "react-router-dom";
import { toast } from "@/hooks/use-toast";

interface MeasurementVariables {
  area_sqft: number;
  eave_lf: number;
  rake_lf: number;
  valley_lf: number;
  hip_lf: number;
  ridge_lf: number;
  sidewall_lf: number;
  headwall_lf: number;
  flashing_lf: number;
  chimney_count: number;
  skylight_count: number;
  pipe_1_5_count: number;
  pipe_3_count: number;
  layers: number;
  eave_extension_ft: number;
}

interface MeasurementVariablesInputProps {
  values: Partial<MeasurementVariables>;
  onChange: (values: Partial<MeasurementVariables>) => void;
}

export function MeasurementVariablesInput({
  values,
  onChange,
}: MeasurementVariablesInputProps) {
  const { estimateId } = useParams();
  const saveTimeoutRef = useRef<NodeJS.Timeout>();

  const handleChange = (field: keyof MeasurementVariables, value: string) => {
    const numValue = parseFloat(value) || 0;
    onChange({ ...values, [field]: numValue });

    // Debounced auto-save after 1 second of inactivity
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    saveTimeoutRef.current = setTimeout(async () => {
      await saveMeasurement(field, numValue);
    }, 1000);
  };

  const saveMeasurement = async (field: keyof MeasurementVariables, value: number) => {
    if (!estimateId) return;

    try {
      // Check if measurement exists
      const { data: existing } = await supabase
        .from('measurements')
        .select('id')
        .eq('estimate_id', estimateId)
        .maybeSingle();

      const measurementData: any = {
        [field]: value,
      };

      // Map frontend field names to database column names
      const fieldMapping: Record<string, string> = {
        'sidewall_lf': 'wall_step_lf',
        'headwall_lf': 'flashing_lf',
        'pipe_1_5_count': 'pipe_count',
        'pipe_3_count': 'fan_count',
        'eave_extension_ft': 'drip_edge_lf', // Using available field
      };

      const dbField = fieldMapping[field] || field;

      if (existing) {
        await supabase
          .from('measurements')
          .update({ [dbField]: value })
          .eq('id', existing.id);
      } else {
        await supabase
          .from('measurements')
          .insert({
            estimate_id: estimateId,
            measurement_type: 'manual',
            [dbField]: value,
          });
      }
    } catch (error: any) {
      console.error('Failed to save measurement:', error);
      toast({
        title: "Auto-save failed",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  const squares = values.area_sqft ? (values.area_sqft / 100).toFixed(2) : '0.00';

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center gap-2">
          <Ruler className="w-5 h-5" />
          Quick Measurements
        </CardTitle>
        <p className="text-sm text-muted-foreground">
          Enter roof measurements to auto-calculate materials
        </p>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Roof Area */}
        <div className="space-y-4 p-4 bg-primary/5 rounded-lg border border-primary/20">
          <div className="flex items-center gap-2 text-primary font-semibold">
            <Home className="w-5 h-5" />
            <span>Roof Area</span>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="area_sqft">Area (sq ft)</Label>
              <Input
                id="area_sqft"
                type="number"
                min="0"
                step="0.01"
                value={values.area_sqft || ''}
                onChange={(e) => handleChange('area_sqft', e.target.value)}
                className="h-12 text-lg"
                placeholder="0"
              />
            </div>
            <div className="space-y-2">
              <Label>Squares (auto)</Label>
              <div className="h-12 px-3 flex items-center bg-muted rounded-md text-lg font-semibold">
                {squares} SQ
              </div>
            </div>
          </div>
        </div>

        {/* Linear Feet Measurements */}
        <div className="space-y-4">
          <div className="flex items-center gap-2 font-semibold">
            <Ruler className="w-5 h-5" />
            <span>Linear Feet (LF)</span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="eave_lf">Eaves</Label>
              <Input
                id="eave_lf"
                type="number"
                min="0"
                step="0.1"
                value={values.eave_lf || ''}
                onChange={(e) => handleChange('eave_lf', e.target.value)}
                placeholder="0"
              />
              <p className="text-xs text-muted-foreground">Perimeter at eaves (ice & water: 3 ft up, gutters)</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="rake_lf">Rakes</Label>
              <Input
                id="rake_lf"
                type="number"
                min="0"
                step="0.1"
                value={values.rake_lf || ''}
                onChange={(e) => handleChange('rake_lf', e.target.value)}
                placeholder="0"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="valley_lf">Valleys</Label>
              <Input
                id="valley_lf"
                type="number"
                min="0"
                step="0.1"
                value={values.valley_lf || ''}
                onChange={(e) => handleChange('valley_lf', e.target.value)}
                placeholder="0"
              />
              <p className="text-xs text-muted-foreground">Ice & water: 36" wide</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="hip_lf">Hips</Label>
              <Input
                id="hip_lf"
                type="number"
                min="0"
                step="0.1"
                value={values.hip_lf || ''}
                onChange={(e) => handleChange('hip_lf', e.target.value)}
                placeholder="0"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="ridge_lf">Ridge</Label>
              <Input
                id="ridge_lf"
                type="number"
                min="0"
                step="0.1"
                value={values.ridge_lf || ''}
                onChange={(e) => handleChange('ridge_lf', e.target.value)}
                placeholder="0"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="flashing_lf">Headwall Flashing</Label>
              <Input
                id="flashing_lf"
                type="number"
                min="0"
                step="0.1"
                value={values.flashing_lf || ''}
                onChange={(e) => handleChange('flashing_lf', e.target.value)}
                placeholder="0"
              />
              <p className="text-xs text-muted-foreground">Horizontal wall above roof (ice & water: 12" wide)</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="sidewall_lf">Sidewall (Step)</Label>
              <Input
                id="sidewall_lf"
                type="number"
                min="0"
                step="0.1"
                value={values.sidewall_lf || ''}
                onChange={(e) => handleChange('sidewall_lf', e.target.value)}
                placeholder="0"
              />
              <p className="text-xs text-muted-foreground">Sloped wall along roof (step flashing: 2.4 pcs/ft)</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="eave_extension_ft">Eave Extension (ft)</Label>
              <Input
                id="eave_extension_ft"
                type="number"
                min="0"
                step="0.1"
                value={values.eave_extension_ft || 3}
                onChange={(e) => handleChange('eave_extension_ft', e.target.value)}
                placeholder="3"
              />
              <p className="text-xs text-muted-foreground">How far ice & water extends up roof (default: 3 ft)</p>
            </div>
          </div>
        </div>

        {/* Counts */}
        <div className="space-y-4">
          <div className="flex items-center gap-2 font-semibold">
            <Layers className="w-5 h-5" />
            <span>Counts & Layers</span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="chimney_count">Chimneys</Label>
              <Input
                id="chimney_count"
                type="number"
                min="0"
                step="1"
                value={values.chimney_count || ''}
                onChange={(e) => handleChange('chimney_count', e.target.value)}
                placeholder="0"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="skylight_count">Skylights</Label>
              <Input
                id="skylight_count"
                type="number"
                min="0"
                step="1"
                value={values.skylight_count || ''}
                onChange={(e) => handleChange('skylight_count', e.target.value)}
                placeholder="0"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="pipe_1_5_count">Pipes (1.5")</Label>
              <Input
                id="pipe_1_5_count"
                type="number"
                min="0"
                step="1"
                value={values.pipe_1_5_count || ''}
                onChange={(e) => handleChange('pipe_1_5_count', e.target.value)}
                placeholder="0"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="pipe_3_count">Pipes (3")</Label>
              <Input
                id="pipe_3_count"
                type="number"
                min="0"
                step="1"
                value={values.pipe_3_count || ''}
                onChange={(e) => handleChange('pipe_3_count', e.target.value)}
                placeholder="0"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="layers">Shingle Layers</Label>
              <Input
                id="layers"
                type="number"
                min="1"
                max="3"
                step="1"
                value={values.layers || 1}
                onChange={(e) => handleChange('layers', e.target.value)}
                placeholder="1"
              />
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
